// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useCallback } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { listApprovals } from '../services/hitlService';
import type { HitlApproval } from '../types';
import { HITL_APPROVAL_STATUS_ORDER } from '../types/hitl';

/** How often to poll HITL for new/updated approvals. */
const POLL_INTERVAL_MS = 30_000;

interface UseApprovalsParams {
  siteId?: string;
  approvalType?: string;
  /** ISO timestamp; only approvals created on/after this time. */
  createdFrom?: string;
  /** ISO timestamp; only approvals created on/before this time. */
  createdTo?: string;
  /** Skip fetching entirely, e.g. when the consumer isn't currently shown. */
  enabled?: boolean;
  /** Re-fetch every poll interval (default). Off for a fixed historical window. */
  poll?: boolean;
}

interface UseApprovalsResult {
  approvals: HitlApproval[];
  loading: boolean;
  /**
   * True whenever a request is in flight — the first load *and* every
   * background poll/refetch. Lets a screen show a subtle "updating…" indicator
   * without blanking the list, whereas `loading` is the first-load-only signal.
   */
  isFetching: boolean;
  error: string | null;
  refetch: () => void;
}

/** Pending first, then Approved, then Rejected; newest first within each rank. */
function sortApprovals(list: HitlApproval[]): HitlApproval[] {
  return [...list].sort((a, b) => {
    const rankDiff =
      (HITL_APPROVAL_STATUS_ORDER[a.state] ?? 3) - (HITL_APPROVAL_STATUS_ORDER[b.state] ?? 3);
    return rankDiff !== 0 ? rankDiff : (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
  });
}

/**
 * Shared query key/fn so every consumer (rail badge, subnav badge, screen)
 * lands on the same cache entry: react-query dedupes the identical requests
 * into a single in-flight fetch and a single 30s poll instead of one per mount.
 */
export function approvalsQueryOptions(
  siteId?: string,
  approvalType?: string,
  createdFrom?: string,
  createdTo?: string
) {
  return queryOptions({
    // `createdTo` joins the key only when set, so the open-ended window every
    // other consumer shares keeps its existing key (and its one poll).
    queryKey: [
      'hitl-approvals',
      siteId ?? null,
      approvalType ?? null,
      createdFrom ?? null,
      ...(createdTo ? [createdTo] : []),
    ] as const,
    queryFn: async () =>
      sortApprovals(await listApprovals({ siteId, approvalType, createdFrom, createdTo })),
  });
}

const NO_APPROVALS: HitlApproval[] = [];

export function useApprovals({
  siteId,
  approvalType,
  createdFrom,
  createdTo,
  enabled = true,
  poll = true,
}: UseApprovalsParams): UseApprovalsResult {
  const query = useQuery({
    ...approvalsQueryOptions(siteId, approvalType, createdFrom, createdTo),
    enabled,
    refetchInterval: poll ? POLL_INTERVAL_MS : false,
  });

  const refetch = useCallback(() => {
    query.refetch();
  }, [query]);

  return {
    approvals: query.data ?? NO_APPROVALS,
    // Only the first load spins; a background poll keeps the last good list.
    loading: enabled && query.isLoading,
    isFetching: enabled && query.isFetching,
    // Swallow a transient poll failure while we still have a list to show.
    error: query.isError && query.data === undefined ? errorMessage(query.error) : null,
    refetch,
  };
}

function errorMessage(err: unknown): string {
  return (err as Error)?.message ?? 'Failed to load approvals';
}
